<template>
  <div class="payment-entry-page">
    <!-- Header Card -->
    <PageHeader icon="fas fa-file-invoice-dollar">
      <template #title> Tambah Pembayaran Klinik </template>
      <template #subtitle>
        Susun layanan, obat, laboratorium, dan pembayaran pasien dalam satu
        transaksi
      </template>
      <template #actions>
        <div class="invoice-number-card invoice-number-card--preview">
          <span>PRATINJAU NOMOR INVOICE</span>
          <strong>{{ invoiceNumberPreview }}</strong>
          <small>Diterbitkan resmi setelah disimpan</small>
        </div>
      </template>
    </PageHeader>

    <b-alert v-if="dentalPlanSource" show variant="info" class="mt-3">
      <i class="fas fa-tooth mr-2"></i>
      Invoice ini berasal dari rencana perawatan
      <strong>{{ dentalPlanSource.plan_number }}</strong
      >. Layanan dan pasien sudah diisi dari versi yang disetujui.
    </b-alert>
    <b-alert v-if="clinicalRecordSource" show variant="info" class="mt-3">
      <i class="fas fa-notes-medical mr-2"></i>
      Invoice ini berasal dari layanan spesialis
      <strong>{{ clinicalRecordSource.record_number }}</strong
      >. Pasien dan layanan sudah diisi dari catatan klinis final.
    </b-alert>

    <Form
      ref="form"
      route="payments"
      :form="form"
      :error="error"
      :isEdit="false"
      :display="display"
      :doctors="doctors"
      :payment_slips="[]"
      :lastValidDiscount="lastValidDiscount"
      @btnCheckoutOnClick="btnCheckoutOnClick"
    />

    <ModalPatient
      :selectedDoctor="form.doctor_id.toString()"
      @chosenPatient="modalPatientOnSelect"
      @submitedPatient="modalPatientOnSelect"
      source="payment"
    />

    <ModalSelectSubPatient
      :patientId="`${form.patient_id}`"
      :patientName="display.patient_name"
      @chosenSubPatient="modalSubPatientOnSelect"
    />

    <ModalMedicine
      :selectedItem="form.medicines_id"
      :isHospitalTransaction="true"
      :checkbox="true"
      :isAbjad="true"
      @chosenMedicine="modalMedicineOnSelect"
    />

    <ModalService
      :selectedItem="form.payment_categories_id"
      :selectedBundle="form.payment_category_bundle_items_id"
      @btnItemBundleOnClick="setServiceBundleItem"
      @btnEditItemBundleOnClick="editServiceBundleItem"
      @btnSeeBundleDetailOnClick="seeServiceBundleItem"
      @checkboxItemOnChange="modalServiceOnSelect"
      @customSubmitedCategory="modalServiceOnSelect"
    />

    <ModalLabProcedure
      :selectedItem="form.lab_procedures_id"
      @chooseItem="modalLabProcedureOnSelect"
    />

    <ModalServiceBundleItem
      ref="modalServiceBundleItem"
      :selectedItem="form.payment_category_bundle_items_id"
      @modalOnSubmit="modalServiceBundleItemOnSubmit"
    />

    <ModalCheckout
      :isHospitalTransaction="true"
      :form="form"
      :error="error"
      @paymentMethodOnClick="paymentMethodOnClick"
      @btnShortCutOnClick="btnShortCutOnClick"
      @inputCashOnKeyUp="inputCashOnKeyUp"
      @btnEWalletOnClick="btnEWalletOnClick"
      @modalCheckoutOnHide="modalCheckoutOnHide"
      @modalCheckoutOnShow="modalCheckoutOnShow"
      @confirmCheckout="confirmCheckout"
    />
  </div>
</template>

<script>
// components
import Form from "@/component/payments/Form.vue";
import ModalPatient from "@/component/general/ModalPatient.vue";
import ModalSelectSubPatient from "@/component/general/ModalSelectSubPatient.vue";
import ModalMedicine from "@/component/general/ModalMedicinePayment.vue";
import ModalService from "@/component/general/ModalPaymentCategory.vue";
import ModalLabProcedure from "@/component/lab-procedures/ModalSelectLabProcedure.vue";
import ModalServiceBundleItem from "@/component/general/ModalPaymentCategoryBundleItem.vue";
import ModalCheckout from "@/component/general/ModalCheckout.vue";

// modules
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { getUser } from "@/core/services/jwt.service.js";
import module from "@/core/modules/CrudModule.js";
import PaymentModule from "@/core/modules/PaymentModule.js";
import validation from "@/core/modules/ValidationModule.js";
import ApiService from "@/core/services/api.service.js";
import JwtService from "@/core/services/jwt.service";

export default {
  components: {
    Form,
    ModalPatient,
    ModalSelectSubPatient,
    ModalMedicine,
    ModalCheckout,
    ModalService,
    ModalLabProcedure,
    ModalServiceBundleItem,
  },

  data() {
    return {
      // display
      display: {
        patient_name: "",
        medicines_name_array: [],
        payment_categories_name_array: [],
        lab_procedures_name_array: [],
      },
      // form
      form: {
        dental_treatment_plan_id: this.$route.query.dental_plan_id ?? null,
        service_channel: this.$route.query.dental_plan_id
          ? "dental"
          : this.$route.query.therapy_case_id
          ? "physiotherapy"
          : this.$route.query.vertical_record_id
          ? "specialist_clinic"
          : null,
        source_type: this.$route.query.dental_plan_id
          ? "dental_treatment_plan"
          : this.$route.query.therapy_case_id
          ? "therapy_case"
          : this.$route.query.vertical_record_id
          ? "vertical_clinical_record"
          : null,
        source_id:
          this.$route.query.dental_plan_id ??
          this.$route.query.therapy_case_id ??
          this.$route.query.vertical_record_id ??
          null,
        payer_type: "patient",
        guarantor_payer_id: null,
        guarantee_reference: null,
        prescription_id: this.$route.query.prescription_id ?? 0,
        patient_id: this.$route.query.patient_id ?? "",
        sub_patient_id: null,
        sub_patient_name: "",
        doctor_id: this.$route.query.doctor_id ?? "",
        appointment_id: this.$route.query.appointment_id ?? "",
        coupon_id: null,
        coupon_name: "",
        coupon_is_percent: 0,
        coupon_percentage_amount: 0,
        coupon_amount: 0,
        discount_reason: "",
        discount_approval_id: null,
        payment_status_id: "",
        bank_id: "",
        bank_account_id: "",
        image: "",
        card_number: "",
        card_type: "",
        edc_struct: "",
        bank_account_number: "",
        bank_account_name: "",
        e_wallet_id: "",
        payment_method_id: 1,
        payment_categories_id: [],
        payment_category_bundle_items_id: [],
        medicines_id: [],
        lab_procedures_id: [],
        surcharge: 0,
        total_amount: 0,
        discount: 0,
        total_promo_amount: 0,
        final_amount: 0,
        tax: 0,
        payment_amount: 0,
        change_amount: 0,
        unpaid_amount: 0,
        count_unpaid_amount: 0,
        notes: "",
        has_usage: 0,
        invoice_notes: "",
        expired_duration: "",
      },
      // error
      error: {
        patient_id: "",
        doctor_id: "",
        payment_categories_id: "",
        medicines_id: "",
        lab_procedures_id: "",
        surcharge: "",
        total_amount: "",
        discount: "",
        final_amount: "",
        tax: "",
        bank_id: "",
        bank_account_id: "",
        image: "",
        card_number: "",
        card_type: "",
        edc_struct: "",
        bank_account_number: "",
        bank_account_name: "",
      },
      // master data
      doctors: [{ label: "Pilih Dokter", id: "", isDisabled: true }],
      // notification
      new_invoice: null,
      // current user
      currentUser: getUser(),
      // image
      image: "",
      // validate
      lastValidDiscount: 0,
      dentalPlanSource: null,
      therapyCaseSource: null,
      clinicalRecordSource: null,
    };
  },

  computed: {
    invoiceNumberPreview() {
      const date = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Jakarta",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
        .format(new Date())
        .replaceAll("-", "");
      return `INV-${date}-******`;
    },
  },

  methods: {
    async getDoctors() {
      this.doctors = await PaymentModule.getDoctors(this.form.patient_id);
    },

    // modal patient @select
    modalPatientOnSelect(value) {
      this.form.patient_id = value.id;
      this.display.patient_name = `${value.name} (${value.patient_id})`;
      if (this.currentUser.user_type_id != 1) {
        this.form.doctor_id = "";
        this.getDoctors();
      }
      this.$bvModal.hide("modal-patient");
      this.$refs.form.modalPatientOnSelect(value);
    },

    // modal sub patient @select
    modalSubPatientOnSelect(value) {
      this.form.sub_patient_id = value.id;
      this.form.sub_patient_name = value.name;
      this.$bvModal.hide("modal-subpatient");
    },

    // modal medicine @select
    modalMedicineOnSelect(value, e) {
      this.$refs.form.modalMedicineOnSelect(value, e);
    },

    // modal service @select
    modalServiceOnSelect(value, index, e) {
      this.$refs.form.modalServiceOnSelect(value);
    },

    // modal lab procedure @select
    modalLabProcedureOnSelect(value) {
      this.$refs.form.modalLabProcedureOnSelect(value);
    },

    // modal service set service bundle item
    setServiceBundleItem(value, index, isChecked) {
      if (!isChecked) {
        this.$bvModal.show("modal-payment-category-bundle-items");
        this.$refs.modalServiceBundleItem.getData(value);
      } else {
        this.$refs.form.modalServiceOnSelect(value);
        let bundleItems = this.form.payment_category_bundle_items_id.filter(
          (x) => x.parent_id == value.id
        );
        this.$refs.form.modalServiceSetServiceBundleItem(bundleItems, value);
      }
    },

    // modal service edit service bundle item
    editServiceBundleItem(value, index, isChecked) {
      this.$bvModal.show("modal-payment-category-bundle-items");
      this.$refs.modalServiceBundleItem.getData(value);
    },

    // modal service see bundle item
    seeServiceBundleItem(value, index) {
      this.$bvModal.show("modal-payment-category-bundle-items");
      this.$refs.modalServiceBundleItem.getData(value);
    },

    // modal service bundle item @submit
    modalServiceBundleItemOnSubmit(bundleItems, paymentCategory) {
      let find = this.form.payment_categories_id.find(
        (x) => x.id == paymentCategory.id
      );
      let edit = false;
      if (paymentCategory.is_bundle == 1) {
        if (typeof find === "undefined") {
          this.$refs.form.modalServiceOnSelect(paymentCategory);
        } else {
          edit = true;
        }
      } else {
        this.$refs.form.modalServiceOnSelect(paymentCategory);
      }
      this.$refs.form.modalServiceSetServiceBundleItem(
        bundleItems,
        paymentCategory,
        edit
      );
    },

    // modal checkout payment method @click
    paymentMethodOnClick(id) {
      this.form.payment_method_id = id;
    },

    // modal checkout btn e-wallet @click
    btnEWalletOnClick(id) {
      this.form.e_wallet_id = id;
    },

    // modal checkout btn cash shortcut @click
    btnShortCutOnClick(amount) {
      if (amount > 0)
        this.form.payment_amount = parseInt(amount).toLocaleString("id-ID");
      else {
        if (this.form.payment_status_id == 2)
          this.form.payment_amount = parseInt(
            this.form.count_unpaid_amount
          ).toLocaleString("id-ID");
        else
          this.form.payment_amount = parseInt(
            this.form.final_amount
          ).toLocaleString("id-ID");
      }
      if (this.form.payment_status_id == 2) {
        this.form.change_amount =
          parseInt(this.form.payment_amount.toString().replace(/\D/g, ""), 10) -
          this.form.unpaid_amount;
        this.form.count_unpaid_amount =
          this.form.unpaid_amount -
          parseInt(this.form.payment_amount.toString().replace(/\D/g, ""), 10);
      } else {
        this.form.change_amount =
          parseInt(this.form.payment_amount.toString().replace(/\D/g, ""), 10) -
          this.form.final_amount;
        this.form.unpaid_amount =
          this.form.final_amount -
          parseInt(this.form.payment_amount.toString().replace(/\D/g, ""), 10);
      }
    },

    // modal checkout input cash @keyup
    inputCashOnKeyUp(evt) {
      if (
        (evt.keyCode >= 48 && evt.keyCode <= 57) ||
        (evt.keyCode >= 96 && evt.keyCode <= 105) ||
        evt.keyCode == 8
      ) {
        this.form.change_amount =
          parseInt(this.form.payment_amount.toString().replace(/\D/g, ""), 10) -
          this.form.final_amount;
        this.form.unpaid_amount =
          this.form.final_amount -
          parseInt(this.form.payment_amount.toString().replace(/\D/g, ""), 10);
      } else {
        evt.preventDefault();
      }
    },

    // modal checkout @hide
    modalCheckoutOnHide() {
      this.error = validation.clearValidationError(this.error);
      this.form.payment_amount = 0;
      this.form.change_amount = 0;
    },

    // modal checkout @show
    modalCheckoutOnShow() {
      this.error = validation.clearValidationError(this.error);
      this.form.payment_amount = 0;
      this.form.change_amount = 0;
    },

    // btn checkout @click
    btnCheckoutOnClick() {
      this.$bvModal.show("modal-checkout");
    },

    // modal checkout @checkout
    async confirmCheckout() {
      if (this.form.payment_method_id != 1) {
        if (
          this.form.payment_amount != 0 &&
          parseInt(this.form.payment_amount.replace(/\D/g, ""), 10) >
            this.form.final_amount
        ) {
          Swal.fire(
            "Gagal",
            "Nominal Pembayaran tidak boleh melebihi Total Tagihan",
            "warning"
          );
          return;
        }
      }
      if (this.form.final_amount > 0 && this.form.payment_amount == 0) {
        Swal.fire("Gagal", "Nominal pembayaran tidak boleh 0", "warning");
        return;
      }
      let form = this.form;
      this.image = this.form.image;

      if (this.form.payment_method_id == 3 && this.form.image != "") {
        form.image = "";
      }

      // if (this.form.payment_method_id == 3 && this.form.image != "") {
      //   form = new FormData();
      //   for (const [key, value] of Object.entries(this.form)) {
      //     if (key == 'payment_categories_id' || key == 'medicines_id' || key == 'lab_procedures_id') {
      //       console.log(value);
      //       form.append(key, JSON.stringify(value))
      //     } else {
      //     form.append(key, value);
      //     }
      //   }
      // }

      let response = await module.submit(form, `payments/checkout`);
      if (response.state == "error") {
        this.error = validation.setValidationError(
          this.error,
          response.error.errors
        );
        return;
      }

      Swal.fire({
        title: response.success.title,
        text: response.success.message,
        icon: "success",
      });
      this.$root.$emit("UpdateNewPayment", "semoga jalan");
      this.getNewInvoice();
      this.$router.push(`/payment/detail/${response.success.payment.id}`);
    },

    // get prescription by id
    async getPrescriptionById() {
      let prescription = await module.get(
        `prescriptions/${this.$route.query.prescription_id}`
      );
      if (prescription) {
        this.display.patient_name = prescription.patient_name;
        this.form.patient_id = prescription.patient_id;
        this.form.doctor_id = prescription.doctor_id;
        // set medicines
        prescription.display_medicine.forEach(async (value, index) => {
          let response = await PaymentModule.getMedicineById(value.id);
          if (typeof response != "undefined") {
            if (response.stock <= 0) {
              Swal.fire(
                "Peringatan",
                `Stok ${response.label} habis`,
                "warning"
              );
              return;
            }
            // push to form medicines_id
            let quantity = 0;
            let arrFrequency = value.frequency.split("+");
            arrFrequency.forEach((ele) => {
              quantity += parseInt(ele);
            });
            quantity = quantity * value.days;
            this.display.medicines_name_array.push({
              id: response.id,
              name: response.label,
            });
            this.form.medicines_id.push({
              label: response.label,
              id: response.id,
              name: response.label,
              promo_id: response.promo_id,
              price: parseInt(response.price),
              const_price: parseInt(response.price) + response.discount_amount,
              promo_amount: parseInt(response.promo_amount),
              stock: response.stock,
              quantity: quantity,
              ref_id: value.ref_id,
              discount_percentage: response.discount_percentage,
              discount_amount: response.discount_amount,
              discount_type: response.discount_type,
              promo: response.promo,
            });
            // sum amount
            this.form.total_promo_amount += parseInt(
              response.promo_amount * quantity
            );
            this.form.total_amount += parseInt(
              (response.const_price - response.promo_amount) * quantity
            );
            this.form.final_amount += parseInt(
              (response.const_price - response.promo_amount) * quantity
            );
          }
        });
      }
    },

    getNewInvoice() {
      ApiService.get("total-new-payments").then((response) => {
        this.new_invoice = response.data;
        JwtService.setNewInvoice(response.data);
      });
    },
    async loadDentalPlan() {
      if (!this.form.dental_treatment_plan_id) return;
      try {
        const response = await ApiService.get(
          `dental/treatment-plans/${this.form.dental_treatment_plan_id}/billing-preview`
        );
        const source = response.data.data;
        if (!source.ready_for_billing) {
          throw new Error(
            "Masih ada tindakan yang belum dipetakan ke layanan kasir."
          );
        }
        if (source.services.some((item) => item.price_changed)) {
          throw new Error(
            "Harga layanan berubah. Revisi rencana dan minta persetujuan pasien terlebih dahulu."
          );
        }
        this.dentalPlanSource = source;
        this.form.service_channel = "dental";
        this.form.source_type = "dental_treatment_plan";
        this.form.source_id = source.plan_id;
        this.form.patient_id = source.patient.id;
        this.display.patient_name = `${source.patient.name} (${source.patient.patient_id})`;
        this.form.payment_categories_id = source.services.map((item) => ({
          ...item,
        }));
        this.display.payment_categories_name_array = source.services.map(
          (item) => ({ id: item.id, name: item.name })
        );
        this.form.total_amount = source.services.reduce(
          (sum, item) =>
            sum +
            (Number(item.const_price) - Number(item.discount_amount || 0)) *
              Number(item.quantity || 1),
          0
        );
        this.form.final_amount = this.form.total_amount;
        this.form.invoice_notes = `Rencana perawatan dental ${source.plan_number}`;
        await this.getDoctors();
      } catch (error) {
        this.form.dental_treatment_plan_id = null;
        const message =
          error.response?.data?.message ||
          error.message ||
          "Rencana dental tidak dapat dimuat ke kasir.";
        Swal.fire("Rencana belum dapat ditagihkan", message, "warning");
      }
    },
    async loadClinicalRecord() {
      if (
        !this.form.source_id ||
        this.form.source_type !== "vertical_clinical_record"
      )
        return;
      try {
        const response = await ApiService.get(
          `vertical-clinical/records/${this.form.source_id}/billing-preview`
        );
        const source = response.data.data;
        this.clinicalRecordSource = source;
        this.form.service_channel = source.service_channel || "specialist_clinic";
        this.form.patient_id = source.patient.id;
        this.form.appointment_id = source.appointment_id || "";
        this.form.doctor_id = source.doctor_id || this.form.doctor_id;
        this.form.payer_type = source.payer_type || "patient";
        this.form.guarantor_payer_id = source.guarantor_payer_id || null;
        this.form.guarantee_reference = source.guarantee_reference || null;
        this.display.patient_name = `${source.patient.name} (${source.patient.patient_id})`;
        this.form.payment_categories_id = source.services.map((item) => ({
          ...item,
        }));
        this.display.payment_categories_name_array = source.services.map(
          (item) => ({ id: item.id, name: item.name })
        );
        this.form.total_amount = source.services.reduce(
          (sum, item) =>
            sum + Number(item.const_price) * Number(item.quantity || 1),
          0
        );
        this.form.final_amount = this.form.total_amount;
        this.form.invoice_notes = `${source.service_name} · ${source.record_number}`;
        await this.getDoctors();
      } catch (error) {
        this.form.source_id = null;
        this.form.source_type = null;
        this.form.service_channel = null;
        const message =
          error.response?.data?.message ||
          error.message ||
          "Layanan spesialis belum dapat dimuat ke kasir.";
        Swal.fire("Layanan belum dapat ditagihkan", message, "warning");
      }
    },
    async loadTherapyCase() {
      if (!this.form.source_id || this.form.source_type !== "therapy_case")
        return;
      try {
        const response = await ApiService.get(
          `therapy/cases/${this.form.source_id}/billing-preview`
        );
        const source = response.data.data;
        this.therapyCaseSource = source;
        this.form.service_channel = "physiotherapy";
        this.form.patient_id = source.patient.id;
        this.display.patient_name = `${source.patient.name} (${source.patient.patient_id})`;
        this.form.payment_categories_id = source.services.map((item) => ({
          ...item,
        }));
        this.display.payment_categories_name_array = source.services.map(
          (item) => ({ id: item.id, name: item.name })
        );
        this.form.total_amount = source.services.reduce(
          (sum, item) =>
            sum + Number(item.const_price) * Number(item.quantity || 1),
          0
        );
        this.form.final_amount = this.form.total_amount;
        this.form.invoice_notes = `Paket terapi · ${source.case_number}`;
        await this.getDoctors();
      } catch (error) {
        this.form.source_id = null;
        this.form.source_type = null;
        this.form.service_channel = null;
        const message =
          error.response?.data?.message ||
          error.message ||
          "Episode terapi belum dapat dimuat ke kasir.";
        Swal.fire("Episode belum dapat ditagihkan", message, "warning");
      }
    },
  },

  watch: {
    "form.patient_id": function (newVal) {
      this.getDoctors();
    },
    "form.doctor_id": function (newVal) {
      if (typeof newVal === "undefined") this.form.doctor_id = "";
    },
  },

  async mounted() {
    // set breadcrumb
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Sales", route: "" },
      { title: "Buat Invoice" },
    ]);
    // set default expired date
    let conf = JSON.parse(localStorage.getItem("configuration"));
    this.form.expired_duration = conf.expired_service;
    // get data
    await this.getDoctors();
    // set form data by query params
    if (this.$route.query != {}) {
      this.form.patient_id = this.$route.query.patient_id;
      this.form.patient_name = this.$route.query.patient_name;
      this.display.patient_name = this.$route.query.patient_name;
      if (typeof this.$route.query.prescription_id != "undefined") {
        this.getPrescriptionById();
      }
    }
    await this.loadDentalPlan();
    await this.loadTherapyCase();
    await this.loadClinicalRecord();
  },
};
</script>

<style scoped>
.invoice-number-card {
  min-width: 230px;
  padding: 9px 13px;
  border: 1px solid #cfe1e8;
  border-radius: 11px;
  background: #f8fbfc;
  text-align: left;
}
.invoice-number-card span,
.invoice-number-card strong,
.invoice-number-card small {
  display: block;
}
.invoice-number-card span {
  color: #168d88;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.09em;
}
.invoice-number-card strong {
  margin: 2px 0;
  color: #173a65;
  font-size: 13px;
  letter-spacing: 0.025em;
}
.invoice-number-card small {
  color: #76879b;
  font-size: 9px;
}
@media (max-width: 767px) {
  .invoice-number-card {
    width: 100%;
    min-width: 0;
  }
}
</style>
